import { SearchInput } from "@/components";
import { MessageItem } from "@/components/ui";
import useBackward from "@/hooks/useBackward";
import useProfile from "@/hooks/useProfile";
import { QUERY_KEYS } from "@/libraries/constant";
import { fromUnixToDateString } from "@/libraries/date";
import { randUuid } from "@/libraries/helper";
import { getCaseQueryOptions, getMessagesQueryOptions } from "@/queries";
import { ChatListSearchSchema } from "@/schemas";
import type { GetCaseResponse, GroupMember, MessageDiscussion } from "@/types";
import Button from "@mui/material/Button";
import {
	onlineManager,
	useSuspenseInfiniteQuery,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import flatMap from "lodash/flatMap";
import groupBy from "lodash/groupBy";
import orderBy from "lodash/orderBy";
import uniqBy from "lodash/uniqBy";
import { useMemo, useRef } from "react";

export const Route = createFileRoute("/dashboard/case/view/$id/search")({
	loader: async ({ context, params }) => {
		if (onlineManager.isOnline() && !context.isRestoring) {
			const caseItem: GetCaseResponse =
				await context.queryClient.ensureQueryData(
					getCaseQueryOptions(params.id),
				);
			const messages = await context.queryClient.ensureInfiniteQueryData(
				getMessagesQueryOptions({
					channel_name: String(caseItem.channel_name),
				}),
			);
			return [caseItem, messages];
		}
		const caseItem = context.queryClient.getQueryData([
			QUERY_KEYS.GET_CASE,
			params.id,
		]) as GetCaseResponse;
		const messages = context.queryClient.getQueryData([
			QUERY_KEYS.GET_MESSAGES,
			{
				channel_name: String(caseItem.channel_name),
			},
		]);
		return [caseItem, messages];
	},
	validateSearch: zodSearchValidator(ChatListSearchSchema),
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const caseId = Route.useParams({
		select: (s) => s.id as number,
	});
	const search = Route.useSearch({
		select: (s) => s.q ?? "",
	});
	const scrollRef = useRef<HTMLDivElement>(null);
	const { profile } = useProfile();
	const { data: caseItem } = useSuspenseQuery(getCaseQueryOptions(caseId));
	const queryMessages = useSuspenseInfiniteQuery(
		getMessagesQueryOptions({
			channel_name: String(caseItem.channel_name),
		}),
	);

	const onBack = useBackward();

	const channelName = caseItem.channel_name;

	const caseMembers = useMemo<GroupMember[]>(() => {
		if (!caseItem.members.length) return [];
		return caseItem.members.filter(
			(m) => m.id !== profile?.id,
		) as GroupMember[];
	}, [caseItem, profile]);
	const memberIds = caseMembers.map((cm) => cm.id);

	const resMessages =
		queryMessages.data?.pages?.flatMap((page) => page.messages) ?? [];

	const messages = useMemo<MessageDiscussion[]>(() => {
		if (!resMessages?.length || !search) return [];
		const hasUserMessages = resMessages.some((m) => !m.system_message);
		if (!hasUserMessages) return resMessages;
		let clonedItems = [...resMessages];
		if (!!search && clonedItems.length) {
			clonedItems = clonedItems.filter((item) =>
				item.text?.toLowerCase()?.includes(search?.toLowerCase()),
			);
		}
		clonedItems = clonedItems.map((clonedItem) => ({
			...clonedItem,
			_isMe: clonedItem.user?.id === profile?.id,
			_flag:
				clonedItem.id_db === -1
					? ("pending" as const)
					: clonedItem.id_db > 0 &&
							(!clonedItem.reads?.length || // When no one read the message or
								(clonedItem.reads.length === 1 && // only the sender read the message
									clonedItem.reads[0] === profile?.id)) // and the sender is the current user
						? ("sent" as const)
						: memberIds.some((id) => !clonedItem.reads.includes(id)) // When not all members read the message
							? ("delivered" as const)
							: memberIds.every((id) => clonedItem.reads.includes(id)) // When all members read the message
								? ("read" as const)
								: ("pending" as const),
		}));
		const orderedMessages = orderBy(clonedItems, "updated_at", "desc");
		const groupedMessages = groupBy(orderedMessages, (m) =>
			fromUnixToDateString(m.updated_at),
		);
		const flatMessages = flatMap(
			groupedMessages,
			(messages, date) =>
				[
					{
						id: randUuid(10),
						text: date,
						system_message: true,
						tags: "date", // Note: You may want to explicitly type this to avoid `@ts-expect-error`
						channel_name: channelName ?? "",
						id_db: 0,
						created_at: messages[0].created_at,
						updated_at: messages[0].updated_at,
						document_count: 0,
						document_size: "0.0 Kb",
						sent: false,
						delivered: false,
						strike: false,
						is_deleted: false,
						reply_to: null,
						invitation_status: null,
						latest_reply_date: null,
						reply_count: 0,
						reads: [],
						mention_name: [],
						user: null,
						parent: null,
						replies: [],
						documents: [],
						mentions: [],
						preview: null,
					},
					...messages,
				] as MessageDiscussion[],
		);
		return uniqBy(flatMessages, "id");
	}, [resMessages, channelName, memberIds, profile?.id, search]);
	const messageCount = messages.length;

	const messageVirtualizer = useVirtualizer({
		enabled: messageCount > 0,
		count: messageCount,
		overscan: 5,
		getScrollElement: () => scrollRef.current,
		measureElement: (el) => el.getBoundingClientRect().height,
		estimateSize: () => 50,
	});
	const messageVirtualizerItems = messageVirtualizer.getVirtualItems();

	return (
		<>
			<main className="flex-1 overflow-hidden">
				<div className="container bg-white py-2 flex flex-row justify-center items-center gap-2">
					<SearchInput
						name="search"
						label="Search"
						placeholder="Search Messages"
						value={search}
						onChange={(e) =>
							navigate({
								replace: true,
								search: {
									q: e.target.value || undefined,
								},
							})
						}
					/>
					<Button size="large" onClick={() => onBack()}>
						Cancel
					</Button>
				</div>
				<div ref={scrollRef} className="relative h-full w-full overflow-auto">
					{messageCount ? (
						<div
							className="relative w-full"
							style={{
								height: messageVirtualizer.getTotalSize(),
							}}
						>
							{messageVirtualizerItems.map((item) => {
								const message = messages[item.index];
								if (!message) return null;
								return (
									<div
										key={item.key}
										ref={messageVirtualizer.measureElement}
										data-index={item.index}
										className="absolute top-0 left-0 w-full"
										style={{
											transform: `translateY(${item.start}px)`,
										}}
									>
										<MessageItem
											chatModule="cases"
											message={message}
											profileId={profile?.id as number}
											className="container py-2 cursor-pointer"
											onClick={() =>
												navigate({
													replace: true,
													to: "/dashboard/case/view/$id",
													params: {
														id: caseId,
													},
													search: {
														mid: message.id_db,
													},
												})
											}
										/>
									</div>
								);
							})}
						</div>
					) : null}
				</div>
			</main>
		</>
	);
}
